import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import blogPosts from '../data/blogPosts';

function renderContent(content) {
  const lines = content.split('\n');
  const elements = [];
  let key = 0;

  for (const line of lines) {
    if (!line.trim()) {
      elements.push(<Box key={key++} sx={{ mb: 1 }} />);
    } else if (line.startsWith('### ')) {
      elements.push(
        <Typography key={key++} component="h3" variant="h6" sx={{ fontWeight: 'bold', mt: 3, mb: 1, color: '#2E7D32' }}>
          {line.replace('### ', '')}
        </Typography>
      );
    } else if (line.startsWith('## ')) {
      elements.push(
        <Typography key={key++} component="h2" variant="h5" sx={{ fontWeight: 'bold', mt: 4, mb: 1.5, color: '#1B5E20' }}>
          {line.replace('## ', '')}
        </Typography>
      );
    } else {
      // Handle bold inline (**text**)
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        return part;
      });
      elements.push(
        <Typography key={key++} variant="body1" sx={{ lineHeight: 1.8, mb: 1.5 }}>
          {rendered}
        </Typography>
      );
    }
  }
  return elements;
}

function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const post = blogPosts.find((p) => p.slug === slug);

  // Loading delay
  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if post not found
  React.useEffect(() => {
    if (!post && !loading) {
      const timer = setTimeout(() => navigate('/blog'), 2000);
      return () => clearTimeout(timer);
    }
  }, [post, loading, navigate]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading post...</Typography>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Post Not Found</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>Redirecting to blog in 2 seconds...</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')}>
          Back to Blog
        </Button>
      </Container>
    );
  }

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      console.log('Invalid date format:', dateStr);
      return "Recently";
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9f9f9', pb: 8 }}>
      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
          py: 6,
          mb: 6,
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blog')}
            sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, '&:hover': { color: 'white' } }}
          >
            Back to Blog
          </Button>

          <Typography
            component="h1"
            variant="h4"
            sx={{ fontWeight: 'bold', mb: 2, lineHeight: 1.3 }}
          >
            {post.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {formatDate(post.date)}
            </Typography>
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 14, color: 'white !important' }} />}
              label={post.readTime}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.75rem' }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md">
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            boxShadow: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
            {post.excerpt}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          {renderContent(post.content)}
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={() => navigate('/')}
            sx={{ mr: 2, borderRadius: 3, fontWeight: 'bold' }}
          >
            Try Earn-E Free
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blog')}
            sx={{ borderRadius: 3 }}
          >
            More Articles
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default BlogPostPage;
